import {
  Stack,
  Box,
  Button as MuiButton,
  Card,
  Divider,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';

import { usePoliciesQuery } from '../../../api/getPolicies';
import AutoHome from '../../../assets/icons/AutoHome';
import colors from '../../../constants/colors';
import useAnalytics from '../../../hooks/useAnalytics';
import CrossSellExpandedCard from '../CrossSellExpandedCard';
import TabletAutoHomeCard from './TabletAutoHomeCard';
import { clubsWithNoMyQuoteLink } from './constants';
import { TextButton } from '../../styledComponents/Button';
import QuoteFormRedirect from '../CrossSellExpandedCard/QuoteFormRedirect';
import { getBundledDiscounts } from '../../../utils/nextGenUtils';
import useGetFeatureFlags from '../../../hooks/useGetFeatureFlags';

const AutoHomeCard = () => {
  const [expandedCardOpen, setExpandedCardOpen] = useState<boolean>(false);
  const { data } = usePoliciesQuery();
  const crossSellInfo = data?.crossSellInfo;
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.only('tablet'));
  const { track } = useAnalytics();

  const clubCode = crossSellInfo?.clubInfo?.clubCode;
  const hasClubLink = !clubsWithNoMyQuoteLink.includes(clubCode as string);

  const { enableNextGen } = useGetFeatureFlags();
  // NEXTGEN-FLAG: on flag retirement, stop passing the flag (see getBundledDiscounts)
  const { autoDiscount, propertyDiscount } = getBundledDiscounts(
    data,
    enableNextGen
  );

  useEffect(() => {
    if (crossSellInfo?.crossSellEligible) {
      track('Targeted Promotion Displayed', {
        event_type: 'Automated System Process',
        contents: 'Bundle and save up to:',
        displayed_auto_discount: crossSellInfo?.bundledDiscounts?.auto,
        displayed_property_discount: crossSellInfo?.bundledDiscounts?.home,
        location: 'Secondary Card',
      });
    }
  }, [crossSellInfo?.crossSellEligible]);

  const handleExpandedCardClose = () => {
    setExpandedCardOpen(false);
  };

  if (matches) return <TabletAutoHomeCard />;

  return (
    <Card
      data-testid="autoHomeCard"
      component={Stack}
      spacing={2}
      sx={{
        boxShadow: '0px 4px 8px rgba(25, 30, 43, 0.08)',
        p: { mobile: 2, tablet: 3, desktop: 2 },
        borderRadius: 2,
        height: 'auto',
      }}
    >
      <Stack spacing={2}>
        <Box
          sx={{
            backgroundColor: colors.csaaGray,
            borderRadius: 1,
            width: 1,
            textAlign: 'center',
            py: 2,
          }}
        >
          <AutoHome />
        </Box>
        <Stack
          spacing={1}
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            width: 1,
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: 16,
              lineHeight: '21px',
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              color: colors.csaaBlack,
            }}
          >
            Bundle and save up to:
          </Typography>
          <MuiButton
            sx={{
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              textTransform: 'none',
              fontSize: 14,
              fontWeight: 700,
              lineHeight: '20px',
              p: 0,
            }}
            onClick={() => {
              track('Promotion Clicked', {
                event_type: 'UX Modified',
                contents: 'Bundle and save up to:',
                link: 'Show more',
                location: 'Secondary Card',
              });
              setExpandedCardOpen(true);
            }}
          >
            Show more
          </MuiButton>
        </Stack>
        <Box
          sx={{
            border: '1px solid',
            borderColor: colors.csaaBorder,
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              fontSize: 14,
              lineHeight: '20px',
              fontWeight: 700,
              textAlign: 'center',
              color: colors.csaaDark,
            }}
          >
            {autoDiscount}% on your auto insurance*
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography
            sx={{
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              fontSize: 14,
              lineHeight: '20px',
              fontWeight: 700,
              textAlign: 'center',
              color: colors.csaaDark,
            }}
          >
            {propertyDiscount}% with AAA Home insurance*
          </Typography>
        </Box>
      </Stack>
      {hasClubLink && (
        <Stack
          direction="row"
          sx={{
            justifyContent: 'flex-end',
          }}
        >
          <QuoteFormRedirect>
            <TextButton
              sx={{ p: 1 }}
              type="submit"
              onClick={() => {
                track('Promotion Clicked', {
                  event_type: 'Link Accessed',
                  contents: 'Bundle and save up to:',
                  link: 'Get a quote',
                  location: 'Secondary Card',
                });
              }}
            >
              Get a quote
            </TextButton>
          </QuoteFormRedirect>
        </Stack>
      )}
      <CrossSellExpandedCard
        open={expandedCardOpen}
        closeDialog={handleExpandedCardClose}
        autoDiscount={autoDiscount}
        propertyDiscount={propertyDiscount}
        hasClubLink={hasClubLink}
      />
    </Card>
  );
};

export default AutoHomeCard;
